import React, { useCallback } from "react";

import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Button,
  Fab,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useLocation, useNavigate } from "react-router-dom";

import { LIST_TOOLBAR_HALF_GAP } from "./dropdownMenuStyles";

import type {
  ButtonProps as MUIButtonProps,
  IconButtonProps,
  SxProps,
  Theme,
} from "@mui/material";
import { GridSearchIcon } from "@mui/x-data-grid-pro";

/** Compact button base — matches Financial Dashboard `.btn` / `.tag-sync`. */
const compactButtonBaseSx: SxProps<Theme> = {
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  letterSpacing: 0,
  lineHeight: 1.2,
  height: "36px",
  minHeight: "36px",
  minWidth: "36px",
  padding: "9px 15px",
  borderRadius: "4px",
  boxShadow: "none",
  "&:hover": {
    boxShadow: "none",
  },
};

/** List/grid toolbar text buttons (Search, Filters, etc.) — 32px tall, 7px/13px padding:
  production geometry (R55-Q1 2026-10-06); Figma Pattern 150 still shows 36px (design follow-up). */
export const listToolbarButtonSx: SxProps<Theme> = {
  ...compactButtonBaseSx,
  height: "32px",
  minHeight: "32px",
  minWidth: "32px",
  padding: "7px 13px",
  mx: { xs: 1, sm: 1.5 },
};

/** List/grid toolbar Filters button (active = tenant accent). */
export const getListToolbarFilterButtonSx = (
  filterActive: boolean
): SxProps<Theme> => ({
  ...listToolbarButtonSx,
  textTransform: "uppercase",
  color: { sm: "white" },
  backgroundColor: { sm: !filterActive ? grey[500] : "primary.main" },
  // Match SEARCH contained hover elevation (theme.shadows[4])
  "&:hover": {
    backgroundColor: {
      sm: !filterActive ? grey[700] : "primary.main",
    },
    boxShadow: (theme) => theme.shadows[4],
  },
  "& .MuiBadge-badge": {
    display: "none",
  },
});

/** List/grid toolbar icon buttons (⋮ menu dots, + FAB chrome size). Keep circular — match ACTIONS 32px. */
export const listToolbarIconButtonSx: SxProps<Theme> = {
  width: 32,
  height: 32,
  minWidth: 32,
  minHeight: 32,
  padding: "6px",
  borderRadius: "50%",
  boxShadow: "none",
  "&:hover": {
    boxShadow: "none",
  },
};

/** Detail header Save / Close buttons. */
export const detailHeaderButtonSx: SxProps<Theme> = {
  ...compactButtonBaseSx,
  alignSelf: "flex-start",
  marginRight: { xs: 1, sm: 1.5 },
};


interface CustomFabProps {
  children: any; // TODO this was React.ReactNode but stopped working after upgrade to material-ui 4.11?
  onClick: (event: any) => any;
  title: string;
  "aria-label": string;
  size?: "small" | "medium" | "large";
  sx?: any;
  disabled?: boolean;
}

export const CustomFab: React.FC<React.PropsWithChildren<CustomFabProps>> = (
  props
) => {
  const { onClick, size, sx, disabled } = props;

  const fab = (
    <Fab
      color="primary"
      aria-label={props["aria-label"]}
      disabled={disabled}
      sx={{
        color: "white",
        "z-index": "1000",
        // No left margin — align with checkbox column (toolbar already has 50px pad)
        marginLeft: 0,
        // Its share of the 18px toolbar gap (see LIST_TOOLBAR_HALF_GAP)
        marginRight: { sm: LIST_TOOLBAR_HALF_GAP },
        ...listToolbarIconButtonSx,
        // Keep the + / action FAB round (Financial Dashboard keeps it round)
        borderRadius: "999px",
        ...sx,
      }}
      onClick={onClick}
      size={size ? size : "small"}
      tabIndex={32766}
    >
      {props.children}
    </Fab>
  );

  // `disabled` is on FabProps but was dropped here, so a "disabled" CreateFab stayed
  // live (Aircraft Patch 5). As CustomFab2: only a disabled Fab gets the tooltip span.
  return <Tooltip title={props.title}>{disabled ? <span>{fab}</span> : fab}</Tooltip>;
};


export const CustomFab2: React.FC<React.PropsWithChildren<CustomFabProps>> = (
  props
) => {
  const { onClick, size, disabled } = props;

  const fab = (
    <Fab
      disabled={disabled}
      color="primary"
      aria-label={props["aria-label"]}
      sx={{
        color: "white",
        "z-index": "1000",
        marginTop: 0,
        marginLeft: 0,
        marginRight: { sm: LIST_TOOLBAR_HALF_GAP },
        ...listToolbarIconButtonSx,
        // Keep the + / action FAB round
        borderRadius: "999px",
      }}
      onClick={onClick}
      size={size ? size : "small"}
      tabIndex={32766}
    >
      {props.children}
    </Fab>
  );

  return (
    <Tooltip title={props.title}>
      {/* A disabled button fires no events, so its tooltip needs a span to hang
          on. Only then: every enabled caller keeps the exact DOM it had. */}
      {disabled ? <span>{fab}</span> : fab}
    </Tooltip>
  );
};
export interface FabProps {
  children?: React.ReactNode;
  onClick: (event: any) => any;
  size?: "small" | "medium" | "large";
  title?: string;
  sx?: any;
  "aria-label"?: string;
  disabled?: boolean;
}

export const CreateFab: React.FC<FabProps> = (props) => {
  const { title = "New",sx } = props;
  return (
    <CustomFab
      title={title}
      aria-label={props["aria-label"] || "add"}
      onClick={props.onClick}
      size={props.size}
      disabled={props.disabled}
      sx = {sx}
    >
      {/* 28px glyph, larger than Figma's 20px and production's 24px (deliberate) */}
      <AddIcon sx={{ fontSize: "28px" }} />
    </CustomFab>
  );
};
export const CreateFab2: React.FC<FabProps> = (props) => {
  const { title = "New" } = props;
  return (
    <CustomFab2
      title={title}
      aria-label={props["aria-label"] || "add"}
      onClick={props.onClick}
      size={props.size}
      disabled={props.disabled}
    >
      {/* 28px glyph, larger than Figma's 20px and production's 24px (deliberate) */}
      <AddIcon sx={{ fontSize: "28px" }} />
    </CustomFab2>
  );
};
export interface SaveFabProps {
  children?: React.ReactNode;
  onClick: (event: any) => any;
  onClick2?: (event: any) => any;
  size?: "small" | "medium" | "large";
  title?: string;
  /** Both buttons greyed — e.g. while a save is in flight, so a second click
   *  cannot send the same write twice. */
  disabled?: boolean;
}

export const SaveFab: React.FC<SaveFabProps> = (props) => {

  return (
      <>
      <Button
        variant="contained"
        onClick={props.onClick2}
        disabled={props.disabled}
        aria-label="save"
        sx={{
          ...detailHeaderButtonSx,
          color: { sm: "white" },
          backgroundColor: { sm: grey[700] },
          "&:hover": { backgroundColor: { sm: grey[900] }, boxShadow: "none" },
        }}
      >
        SAVE
      </Button>
      <Button
        variant="contained"
        onClick={props.onClick}
        disabled={props.disabled}
        sx={{
          ...detailHeaderButtonSx,
          padding: "9px 17px",
          "&:hover": { boxShadow: "none" },
        }}
      >
        SAVE & CLOSE
      </Button>
      </>

  );
};

export const SearchFab: React.FC<FabProps> = (props) => {
  return (
    <CustomFab
      title="Search"
      aria-label="search"
      onClick={props.onClick}
      size={props.size}
    >
      <ManageSearchIcon />
    </CustomFab>
  );
};

export interface ButtonProps extends MUIButtonProps {
  children?: React.ReactNode;
  onClick: (event: any) => any;
}

export const CancelButton: React.FC<React.PropsWithChildren<ButtonProps>> = (
  props
) => {
  const { children = "Cancel", onClick, disabled } = props;
  return <Button children={children} onClick={onClick} disabled={disabled} />;
};

export const ActionButton: React.FC<React.PropsWithChildren<ButtonProps>> = (
  props
) => {
  const { children, onClick, disabled } = props;
  return (
    <Button
      variant="contained"
      color="primary"
      children={children}
      onClick={onClick}
      disabled={disabled}
    />
  );
};

interface MenuButtonProps extends MUIButtonProps {
  onClick: (event: any) => any;
}

export const MenuButton: React.FC<MenuButtonProps> = ({ sx, ...props }) => {
  return (
    <IconButton
      aria-controls="options-menu"
      aria-haspopup="true"
      sx={{
        color: { sm: "white" },
        marginLeft: { xs: 1, sm: 1.5 },
        backgroundColor: { sm: "var(--an-action-subtle, #d9d9d9)" },
        "&:hover": { backgroundColor: { sm: grey[400] }, boxShadow: "none" },
        ...listToolbarIconButtonSx,
        // Placement only (margins); no caller passed sx before this.
        ...(sx as object),
      }}
      size="small"
      {...props}
    >
      <MoreVertIcon sx={{ color: { sm: "white" }, fontSize: "20px" }} />
    </IconButton>
  );
};

interface ClearButtonProps {
  onClick: () => void;
  value: string;
}

export const ClearButton: React.FC<ClearButtonProps> = (
  props: ClearButtonProps
) => {
  return (
    <IconButton
      title="Clear"
      aria-label="Clear"
      size="small"
      sx={{ visibility: props.value ? "visible" : "hidden", color: "white" }}
      onClick={props.onClick}
    >
      <ClearIcon fontSize="small" />
    </IconButton>
  );
};

interface PDFButtonProps {
  href: string;
}

export const PDFButton: React.FC<PDFButtonProps> = (props: PDFButtonProps) => {
  const { href } = props;
  return (
    <IconButton
      component={Link}
      href={href}
      rel="noopener noreferrer"
      onClick={(event: React.MouseEvent<HTMLElement>) =>
        event.stopPropagation()
      }
      size="small"
      sx={{ color: grey[400] }}
    >
      <PictureAsPdfIcon />
    </IconButton>
  );
};


export interface CloseButtonProps {
  onClose?: (event: any) => any;
  closeUrl?: string;
}

export const CloseButton: React.FC<CloseButtonProps> = (props) => {
  const { onClose, closeUrl } = props;
  const navigate = useNavigate();
  // "default" is the router's key for the FIRST entry of this browser tab: nothing in the
  // app is behind it, so navigate(-1) would do nothing (a record opened in a new tab — Luis
  // 2026-10-12, "clicking Close does nothing"). Then Close goes up to the record's list.
  const { key: entry } = useLocation();

  const onClick = useCallback(
    (event) => {
      if (onClose) {
        onClose(event);
      } else if (closeUrl) {
        navigate(closeUrl);
      } else if (entry === "default") {
        navigate("..");
      } else {
        navigate(-1);
      }
    },
    [closeUrl, entry, navigate, onClose]
  );

  return (
      <Button
        color="inherit"
        onClick={onClick}
        aria-label="close"
        sx={{
          ...detailHeaderButtonSx,
          color: { sm: "white" },
          backgroundColor: { sm: grey[500] },
          "&:hover": { backgroundColor: { sm: grey[700] }, boxShadow: "none" },
        }}
      >
        CLOSE
      </Button>

  );
};

// `sx` and `style` are deliberately NOT accepted, as for SearchButton: the header palette lives
// here, beside CLOSE / SAVE, so a Figma change to the header row is a one-file change.
type HeaderMenuButtonProps = Omit<IconButtonProps, "sx" | "style" | "children">;

/**
 * A menu button in a record's HEADER row — the record actions ⋮ (BP-09), left of CLOSE.
 *
 * Aaron 2026-09-30 (mustang flight record): "move the print menu up to the header row with
 * the correct colours for a button menu here". So it wears the header buttons' own chrome
 * (`detailHeaderButtonSx`: 36px, radius 4, the same right-hand gap) and CLOSE's palette —
 * a white ⋮ on grey[500], grey[700] on hover — and never the list toolbar's pale round
 * `MenuButton`, which reads as a grey circle in a white square on the grey header.
 */
export const HeaderMenuButton = React.forwardRef<HTMLButtonElement, HeaderMenuButtonProps>(
  function HeaderMenuButton(props, ref) {
    return (
      <IconButton
        ref={ref}
        {...props}
        sx={{
          ...detailHeaderButtonSx,
          width: "36px",
          padding: "6px",
          color: { sm: "white" },
          backgroundColor: { sm: grey[500] },
          "&:hover": { backgroundColor: { sm: grey[700] }, boxShadow: "none" },
          "&.Mui-disabled": {
            color: { sm: "white" },
            backgroundColor: { sm: grey[300] },
          },
        }}
      >
        <MoreVertIcon />
      </IconButton>
    );
  }
);

// `sx` and `style` are deliberately NOT accepted: the colour lives here and only here, so a
// Figma change to Pattern 150 is a one-file change. Call sites pass `active`.
// Enforced twice: the Omit below (tsc) and no-restricted-syntax (eslint).
interface SearchButtonProps extends Omit<MUIButtonProps, "sx" | "style"> {
  /** Figma module-list Search is primary even before a search is open. */
  appearance?: "primary";
  onClick: (event: any) => any;
  /** Advanced search is open (the toolbar's `advanceSearchTriggered`). */
  active?: boolean;
}

/**
 * @figma Component 22 · Button — Search, in Pattern 150 · List Toolbar
 * Idle wears Figma color/action/neutral (hover neutral-hover). Active wears
 * the tenant accent (primary.main). Pattern 153 opts into primary at rest.
 * https://www.figma.com/design/HDBjSeAYxCpDOHrBaib9ik/Tools?node-id=3-1366
 */
export const SearchButton: React.FC<SearchButtonProps> = ({ active = false, appearance, ...props }) => (
  <Button
    {...props}
    variant="contained"
    startIcon={<GridSearchIcon />}
    sx={{
      ...listToolbarButtonSx,
      color: "white",
      backgroundColor: active || appearance === "primary" ? "primary.main" : "var(--an-action-neutral)",
      "&:hover": {
        backgroundColor: active || appearance === "primary" ? "primary.main" : "var(--an-action-neutral-hover)",
        boxShadow: (theme) => theme.shadows[4],
      },
    }}
  >
    SEARCH
  </Button>
);
